import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import webhookService from '../../services/webhookService';

const initialState = {
  webhooks: [],
  currentWebhook: null,
  logs: [],
  logsPagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  loading: false,
  error: null
};

export const fetchWebhooks = createAsyncThunk(
  'webhooks/fetchAll',
  async (params, thunkAPI) => {
    try {
      const response = await webhookService.getWebhooks(params);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch webhooks';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchWebhookById = createAsyncThunk(
  'webhooks/fetchById',
  async (id, thunkAPI) => {
    try {
      const response = await webhookService.getWebhookById(id);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch webhook';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createWebhook = createAsyncThunk(
  'webhooks/create',
  async (data, thunkAPI) => {
    try {
      const response = await webhookService.createWebhook(data);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create webhook';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateWebhook = createAsyncThunk(
  'webhooks/update',
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await webhookService.updateWebhook(id, data);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update webhook';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteWebhook = createAsyncThunk(
  'webhooks/delete',
  async (id, thunkAPI) => {
    try {
      await webhookService.deleteWebhook(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete webhook';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const regenerateSecret = createAsyncThunk(
  'webhooks/regenerateSecret',
  async (id, thunkAPI) => {
    try {
      const response = await webhookService.regenerateSecret(id);
      return { id, secret_key: response.data.secret_key };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to regenerate secret';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchWebhookLogs = createAsyncThunk(
  'webhooks/fetchLogs',
  async (params, thunkAPI) => {
    try {
      const response = await webhookService.getWebhookLogs(params);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch webhook logs';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resendWebhook = createAsyncThunk(
  'webhooks/resend',
  async (logId, thunkAPI) => {
    try {
      const response = await webhookService.resendWebhook(logId);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to resend webhook';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const webhookSlice = createSlice({
  name: 'webhooks',
  initialState,
  reducers: {
    clearCurrentWebhook: (state) => {
      state.currentWebhook = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWebhooks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWebhooks.fulfilled, (state, action) => {
        state.loading = false;
        state.webhooks = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchWebhooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWebhookById.fulfilled, (state, action) => {
        state.currentWebhook = action.payload;
      })
      .addCase(createWebhook.pending, (state) => {
        state.loading = true;
      })
      .addCase(createWebhook.fulfilled, (state, action) => {
        state.loading = false;
        state.webhooks.unshift(action.payload);
      })
      .addCase(createWebhook.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateWebhook.fulfilled, (state, action) => {
        const index = state.webhooks.findIndex(w => w.id === action.payload.id);
        if (index !== -1) {
          state.webhooks[index] = action.payload;
        }
      })
      .addCase(deleteWebhook.fulfilled, (state, action) => {
        state.webhooks = state.webhooks.filter(w => w.id !== action.payload);
      })
      .addCase(regenerateSecret.fulfilled, (state, action) => {
        const webhook = state.webhooks.find(w => w.id === action.payload.id);
        if (webhook) {
          webhook.secret_key = action.payload.secret_key;
        }
        if (state.currentWebhook?.id === action.payload.id) {
          state.currentWebhook.secret_key = action.payload.secret_key;
        }
      })
      .addCase(fetchWebhookLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWebhookLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.data;
        state.logsPagination = action.payload.pagination;
      })
      .addCase(fetchWebhookLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearCurrentWebhook, clearError } = webhookSlice.actions;
export default webhookSlice.reducer;
